import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import routes from '../../../utils/constants/routes';
import useGetMoreActionsContent from '../data/useGetMoreActionsContent';
import colors from '../../../styled/colors';
import { TriangleIcon, CircleLinesIcon } from '../iconsSVG/icons';
import { Button } from '../../../components/buttons';
import mediaQueries from '../../../utils/mediaQueries';

const Section = styled.section`
  background: ${colors.white};
  height: 100%;
  padding-top: 62px;
  padding-bottom: 106px;
  @media ${mediaQueries.mdUp} {
    padding: 88px 85px 73px;
  }
  @media ${mediaQueries.lgPlusUp} {
    height: auto;
    padding: 88px 36px 63px;
  }
  @media ${mediaQueries.xlPlusUp} {
    padding: 110px 151px;
  }
`;
const TitleContainer = styled.div``;
const Title = styled.h2`
  color: ${colors.black3E};
  font-size: 18px;
  font-weight: normal;
  text-align: center;
  position: relative;
  z-index: 1;
  @media ${mediaQueries.mdUp} {
    font-size: 22px;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 28px;
    strong {
      font-size: 31px;
    }
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size:32px;
    strong {
      font-size: 36px;
    }
  }
  &:before {
    background: ${colors.lightBlue};
    border-radius: 50%;
    content: '';
    width: 38px;
    height: 38px;
    opacity: 0.5;
    position: absolute;
    top: -12px;
    right: calc(50% - 74px);
    z-index: -1;
    @media ${mediaQueries.lgPlusUp} {
      width: 76px;
      height: 76px;
      top: -16px;
      right: calc(50% - 108px);
    }
  }
  svg {
    position: absolute;
    z-index: -1;
  }
  .triangle {
    top: 22px;
    left: calc(50% - 131px);
    @media ${mediaQueries.lgPlusUp} {
      width: 29px;
      height: 29px;
      stroke-width: 0.5px;
      top: 36px;
      left: calc(50% - 194px);
    }
  }
  .circle-lines {
    top: 8px;
    right: calc(50% - 162px);
    @media ${mediaQueries.lgPlusUp} {
      width: 49px;
      height: 57px;
      stroke-width: 0.5px;
      top: 18px;
      right: calc(50% - 274px);
    }
    @media ${mediaQueries.xlPlusUp} {
      top: 4px;
      right: calc(50% - 336px);
    }
  }
`;
const Content = styled.div`
  margin-top: 40px;
  @media ${mediaQueries.mdUp} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 122px;
    grid-row-gap: 58px;
    margin-top: 63px;
  }
  @media ${mediaQueries.lgPlusUp} {
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: 20px;
    margin-top: 82px;
  }
  @media ${mediaQueries.xlPlusUp} {
    grid-column-gap: 46px;
  }
`;
const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  @media ${mediaQueries.lgPlusUp} {
    height: fit-content;
  }
  &:not(:last-child) {
    margin-bottom: 44px;
    @media ${mediaQueries.mdUp} {
      margin-bottom: 0;
    }
  }
`;
const ImageContainer = styled.picture`
  display: block;
  max-width: 120px;
  max-height: 120px;
  @media ${mediaQueries.mdUp} {
    max-width: 125px;
    max-height: 125px;
  }
  @media ${mediaQueries.lgPlusUp} {
    max-width: 117px;
    max-height: 117px;
  }
  @media ${mediaQueries.xlPlusUp} {
    max-width: 150px;
    max-height: 150px;
  }
`;
const Image = styled.img`
  width: 100%;
`;
const Description = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
  width: 100%;
  @media ${mediaQueries.mdUp} {
    margin-top: 22px;
  }
`;
const DescriptionText = styled.p`
  color: ${colors.black3E};
  font-size: 14px;
  line-height: 16px;
  margin: 0;
  text-align: center;
  max-width: 250px;
  @media ${mediaQueries.mdUp} {
    font-size: 16px;
  }
  @media ${mediaQueries.lgPlusUp} {
    font-size: 14px;
    line-height: 22px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 18px;
    line-height: 26px;
  }
`;
const CTAContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 90px;
  @media ${mediaQueries.lgPlusUp} {
    margin-top: 87px;
  }
  @media ${mediaQueries.xlPlusUp} {
    margin-top: 110px;
  }
`;
const ButtonContainer = styled.div``;
const CustomButton = styled(Button)`
  && {
    padding-left: 22px;
    padding-right: 22px;
    @media ${mediaQueries.lgPlusUp} {
      font-size: 24px;
    }
  }
`;
const CTADescription = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 22px;
  @media ${mediaQueries.lgPlusUp} {
    margin-top: 16px;
  }
  @media ${mediaQueries.xlPlusUp} {
    margin-top: 38px;
  }
`;
const CTADescriptionText = styled.p`
  color: ${colors.black3E};
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  max-width: 185px;
  margin: 0;
  @media ${mediaQueries.lgPlusUp} {
    font-size: 15px;
    max-width: max-content;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 18px;
  }
`;
const Action = ({ action }) => (
  <Container>
    <ImageContainer>
      <source srcSet={action.imageWebp} />
      <source srcSet={action.image} />
      <Image src={action.image} />
    </ImageContainer>
    <Description>
      <DescriptionText>
        {action.description}
      </DescriptionText>
    </Description>
  </Container>
);

Action.propTypes = {
  action: PropTypes.shape({
    key: PropTypes.string,
    image: PropTypes.string,
    imageWebp: PropTypes.string,
    description: PropTypes.node,
  }).isRequired,
};

const MoreActions = ({ daysTrial }) => {
  const moreActionsContent = useGetMoreActionsContent();
  const history = useHistory();
  return (
    <Section>
      <TitleContainer>
        <Title>
          <FormattedMessage
            id="landing.instagram.moreactions.title"
            defaultMessage="Also, in <s>Social Piper, you can:</s>"
            values={{
              s: (...chunks) => <strong>{chunks}</strong>,
            }}
          />
          <TriangleIcon className="triangle" />
          <CircleLinesIcon className="circle-lines" />
        </Title>
      </TitleContainer>
      <Content>
        {moreActionsContent.map((action) => (
          <Action action={action} key={action.key} />
        ))}
      </Content>
      <CTAContainer>
        <ButtonContainer>
          <CustomButton onClick={() => history.push(routes.PRICING)}>
            <FormattedMessage
              id="landing.instagram.moreactions.cta.button"
              defaultMessage="Start Your {daysTrial}-Day Trial"
              values={{ daysTrial }}
            />
          </CustomButton>
        </ButtonContainer>
        <CTADescription>
          <CTADescriptionText>
            <FormattedMessage
              id="landing.instagram.moreactions.cta.description"
              defaultMessage="Try Social Piper risk-free, you can cancel at any time."
            />
          </CTADescriptionText>
        </CTADescription>
      </CTAContainer>
    </Section>
  );
};

MoreActions.propTypes = {
  daysTrial: PropTypes.number.isRequired,
};

export default MoreActions;
